import {
  type Connection,
  PACKET_DATA_SIZE,
  PublicKey,
  SYSVAR_RENT_PUBKEY,
  type TransactionInstruction,
  TransactionMessage,
  VersionedTransaction,
} from '@solana/web3.js';
import { estimateComputeUnits } from '../constants/cu-table.js';
import {
  MORFI_OPINION_PROGRAM_ID,
  SYSTEM_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
} from '../constants/programs.js';
import { getProgram } from '../idl/loader.js';
import {
  deriveClaimRightsMintPda,
  deriveEventAuthorityPda,
  deriveOutcomeMintPda,
  deriveTokenMetadataPda,
} from '../pda/derivations.js';
import type { BuildParams, BuildResult } from '../types/build-result.js';
import { createBuildResult, invokeMethod, uniqueSignerKeys } from './common.js';
import { deriveCreationPda } from './market-creation-addresses.js';

const TOKEN_METADATA_PROGRAM_ID: PublicKey = new PublicKey(
  'metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s',
);

export interface OutcomePreparation {
  outcomeIndex: number;
  name: string;
  symbol: string;
  uri: string;
}

export interface PrepareOutcomesParams extends BuildParams {
  creator: PublicKey;
  payer?: PublicKey;
  market: PublicKey;
  marketConfig: PublicKey;
  /** Existing schedule for timed markets; null for conviction markets. */
  eventSchedule: PublicKey | null;
  outcomes: readonly OutcomePreparation[];
}

export interface OutcomePreparationBatch {
  outcomes: readonly OutcomePreparation[];
  build: BuildResult;
}

/** Prepare one atomic batch, with deterministic outcome mints and no mint signers. */
export async function buildPrepareOutcomes(
  connection: Connection,
  params: PrepareOutcomesParams,
): Promise<BuildResult> {
  validateOutcomes(params.outcomes);
  const build = await buildPreparation(connection, params);
  if (!fitsPacket(build, params.payer ?? params.creator)) {
    throw new Error(
      'Outcome preparation exceeds transaction packet size; use buildPrepareOutcomeBatches',
    );
  }
  return build;
}

/**
 * Pack up to two outcomes per transaction, reducing the batch when metadata exceeds
 * the packet limit. Size includes compute budget and creator/payer signatures.
 * Callers adding instructions or a different fee payer must recheck final size.
 */
export async function buildPrepareOutcomeBatches(
  connection: Connection,
  params: PrepareOutcomesParams,
): Promise<OutcomePreparationBatch[]> {
  validateOutcomes(params.outcomes);
  const batches: OutcomePreparationBatch[] = [];
  let offset = 0;
  while (offset < params.outcomes.length) {
    let outcomes = params.outcomes.slice(offset, offset + 2);
    let build = await buildPreparation(connection, { ...params, outcomes });
    if (!fitsPacket(build, params.payer ?? params.creator)) {
      outcomes = outcomes.slice(0, 1);
      build = await buildPreparation(connection, { ...params, outcomes });
      if (!fitsPacket(build, params.payer ?? params.creator)) {
        throw new Error(
          `Outcome preparation ${String(outcomes[0]?.outcomeIndex)} exceeds transaction packet size`,
        );
      }
    }
    batches.push({ outcomes, build });
    offset += outcomes.length;
  }
  return batches;
}

async function buildPreparation(
  connection: Connection,
  params: PrepareOutcomesParams,
): Promise<BuildResult> {
  const instruction = await buildPrepareOutcomesInstruction(connection, params);
  return createBuildResult(
    [instruction],
    uniqueSignerKeys(params.creator, params.payer ?? params.creator),
    params.computeUnits ?? estimateComputeUnits('prepare_outcomes'),
  );
}

export async function buildPrepareOutcomesInstruction(
  connection: Connection,
  params: PrepareOutcomesParams,
): Promise<TransactionInstruction> {
  const programId = params.programId ?? MORFI_OPINION_PROGRAM_ID;
  const payer = params.payer ?? params.creator;
  const program = await getProgram(connection, programId);
  const remainingAccounts = params.outcomes.flatMap(({ outcomeIndex }) => {
    const [mint] = deriveOutcomeMintPda(params.market, outcomeIndex, programId);
    const [inventory] = PublicKey.findProgramAddressSync(
      [Buffer.from('outcome_token_vault'), params.market.toBuffer(), Buffer.from([outcomeIndex])],
      programId,
    );
    const [claimMint] = deriveClaimRightsMintPda(params.market, outcomeIndex, programId);
    return [mint, inventory, claimMint, deriveTokenMetadataPda(mint)[0]].map((pubkey) => ({
      pubkey,
      isSigner: false,
      isWritable: true,
    }));
  });
  return await invokeMethod(
    program,
    'prepareOutcomes',
    params.outcomes.map((outcome) => ({ ...outcome })),
  )
    .accounts({
      creator: params.creator,
      payer,
      market: params.market,
      marketConfig: params.marketConfig,
      outcomeMintAuthority: deriveCreationPda(
        'outcome_mint_authority',
        params.market,
        programId,
      )[0],
      tokenMetadataProgram: TOKEN_METADATA_PROGRAM_ID,
      systemProgram: SYSTEM_PROGRAM_ID,
      tokenProgram: TOKEN_PROGRAM_ID,
      rent: SYSVAR_RENT_PUBKEY,
      eventSchedule: params.eventSchedule,
      eventAuthority: deriveEventAuthorityPda(programId)[0],
      program: programId,
    })
    .remainingAccounts(remainingAccounts)
    .instruction();
}

function fitsPacket(build: BuildResult, payer: PublicKey): boolean {
  const message = new TransactionMessage({
    payerKey: payer,
    recentBlockhash: '11111111111111111111111111111111',
    instructions: build.instructions,
  }).compileToV0Message();
  try {
    return new VersionedTransaction(message).serialize().length <= PACKET_DATA_SIZE;
  } catch (error) {
    if (error instanceof RangeError) return false;
    throw error;
  }
}

function validateOutcomes(outcomes: readonly OutcomePreparation[]): void {
  if (outcomes.length === 0 || outcomes.length > 10)
    throw new Error('Preparation requires 1..10 outcomes');
  const indices = new Set<number>();
  for (const outcome of outcomes) {
    if (
      !Number.isInteger(outcome.outcomeIndex) ||
      outcome.outcomeIndex < 0 ||
      outcome.outcomeIndex >= 10
    ) {
      throw new Error('Outcome index must be an integer in 0..9');
    }
    if (indices.has(outcome.outcomeIndex))
      throw new Error('Duplicate outcome index in preparation');
    indices.add(outcome.outcomeIndex);
  }
}
